/*
 *   Copyright (c) 2021 Busy Human LLC
 *   All rights reserved.
 *   This file, its contents, concepts, methods, behavior, and operation
 *   (collectively the "Software") are protected by trade secret, patent,
 *   and copyright laws. The use of the Software is governed by a license
 *   agreement. Disclosure of the Software to third parties, in any form,
 *   in whole or in part, is expressly prohibited except as authorized by
 *   the license agreement.
 */

import { User } from "firebase/auth";
import rg4js from "raygun4js";
import { App } from "vue";
const LOCAL_STORAGE_KEY = "raygun-enabled"; // Set raygun-enabled to true in localStorage to force it on

/**
 * Usage:
 *
 *  initRaygun('KEY');   // Note: Raygun is only initialized in production, or with the presence of a key in localStorage set manually by a developer
 *  setRaygunUserEventGenerator(function() {
        return {
            identifier: user.uid,
            isAnonymous: !Boolean(!user),
            email: user.email || null
        }
    });
 */

/**
 * Object for tracking global raygun settings/helpers
 */
const raygun: {userEventGenerator: RaygunUserEventGenerator | null} = {
    /**
     * @type {RaygunUserEventGenerator}
     */
    userEventGenerator: null
};

/**
 * @typedef {object} RaygunUserEvent
 * @property {string} identifier
 * @property {boolean} isAnonymous
 * @property {string} email
 */
type RaygunUserEvent = {
    identifier: string
    isAnonymous: boolean
    email: string
}

type RaygunUserEventGenerator = (userData: User | null) => RaygunUserEvent

/**
 * Return whether or not raygun should be enabled
 * @returns {Boolean}
 */
export function raygunIsEnabled(): boolean {
    try {
        if(ENVIRONMENT === "production") {
            return true;
        }
        var overrideStr = localStorage.getItem(LOCAL_STORAGE_KEY);
        if(overrideStr && JSON.parse(overrideStr) === true) {
            return true;
        }
        return false;
    } catch(err) {
        console.error(err);
        return true;
    }
}

/**
 * Emits a page view to raygun
 * @param {string} path
 */
export function raygunPageView(path: string) {
    if(raygunIsEnabled()) {
        rg4js('trackEvent', {
            type: 'pageView',
            path
        });
    }
}

/**
 * Initializes raygn with its APIKey
 * @param {string} key
 * @param {*} [options]
 */
export function initRaygun(key: string, {Vue}: {Vue?: App, [key: string]: any}={}) {
    if(!key) {
        throw new Error(`initRaygun( key ) requires key`);
    }
    if(raygunIsEnabled()) {
        rg4js('apiKey', key);
        rg4js('enableCrashReporting', true);
        rg4js('enablePulse', true);

        if(Vue) {
            configureVueErrorHandler(Vue);
        } else {
            throw new Error(`options.Vue is required`);
        }
    }
}

/**
 * Custom code for configuring Vue
 */
function configureVueErrorHandler(Vue: App) {
    Vue.config.errorHandler = function(err, vm, info) {
        rg4js("send", {
            error: err,
            customData: [{ info }]
        });
    };
}

/**
 * Set the function used to generate the raygun user event
 * @param {RaygunUserEventGenerator} fn
 */
export function setRaygunUserEventGenerator(fn: RaygunUserEventGenerator) {
    if(typeof fn !== "function") {
        throw new Error(`userEventGenerator must be a function`);
    }
    raygun.userEventGenerator = fn;
    updateRaygunUser(null);
}

/**
 * Updates raygun with the latest user data, providing it directly if needed
 * @param {*} [userData] - data passed to userEventGenerator to either replace or update the user if needed
 */
export function updateRaygunUser(userData: User | null) {
    if( ! raygun.userEventGenerator ) {
        throw new Error(`Must define userEventGenerator via setRaygunUserEventGenerator first`);
    }
    if(raygunIsEnabled()) {
        /** @type {RaygunUserEvent} */
        var evt = raygun.userEventGenerator(userData);
        rg4js('setUser', evt);
    }
}

export function recordBreadcrumb(message: string, meta: Record<string, string>) {
    rg4js("recordBreadcrumb", message, meta);
}

/**
 * Set the tags for the app
 * @param {string[]} tags
 */
export function setTags(tags: string[]) {
    rg4js("withTags", tags);
}